import {HTMLProps} from "react";

import {autoUpdate, flip, offset, Placement, shift, size, useDismiss, useFloating, useInteractions} from "@floating-ui/react";

interface UseFloatingDropdownProps {
    isOpen: boolean;
    onOpenChange?: (open: boolean) => void;
    placement?: Placement;
    offsetValue?: number;
    shiftPadding?: number;
    enableAutoScroll?: boolean;
    maxHeightPadding?: number;
    maxHeight?: number; // Maximum height in pixels (e.g., 400)
    disableFlip?: boolean; // If true, dropdown stays at initial placement and never flips
    matchReferenceWidth?: boolean;
}

interface UseFloatingDropdownReturn {
    refs: ReturnType<typeof useFloating>["refs"];
    floatingStyles: ReturnType<typeof useFloating>["floatingStyles"];
    context: ReturnType<typeof useFloating>["context"];
    getReferenceProps: (userProps?: HTMLProps<Element>) => Record<string, unknown>;
    getFloatingProps: (userProps?: HTMLProps<HTMLElement>) => Record<string, unknown>;
}

export function useFloatingDropdown({
    isOpen,
    onOpenChange,
    placement = "bottom-start",
    offsetValue = 8,
    shiftPadding = 8,
    enableAutoScroll = false,
    maxHeightPadding = 16,
    maxHeight,
    disableFlip = false,
    matchReferenceWidth = true,
}: UseFloatingDropdownProps): UseFloatingDropdownReturn {
    // Floating UI for automatic positioning
    const {refs, floatingStyles, context} = useFloating({
        open: isOpen,
        onOpenChange,
        placement,
        middleware: [
            offset(offsetValue ?? 8),
            ...(disableFlip ? [] : [flip()]),
            shift({padding: shiftPadding}),
            size({
                apply({availableHeight, elements, rects}) {
                    const styles: Record<string, string> = {};

                    // Set min-width to match the trigger width
                    if (matchReferenceWidth) {
                        styles.minWidth = `${rects.reference.width}px`;
                    }

                    // Automatically limit height and add scroll if content doesn't fit
                    if (enableAutoScroll) {
                        const calculatedMaxHeight = availableHeight - maxHeightPadding;
                        // Use provided maxHeight if it's smaller than available space
                        const finalMaxHeight = maxHeight ? Math.min(maxHeight, calculatedMaxHeight) : calculatedMaxHeight;
                        styles.maxHeight = `${finalMaxHeight}px`;
                        styles.overflowY = "auto";
                    }

                    Object.assign(elements.floating.style, styles);
                },
            }),
        ],
        whileElementsMounted: autoUpdate,
    });

    // Handle outside click to close dropdown
    const dismiss = useDismiss(context);

    const {getReferenceProps, getFloatingProps} = useInteractions([dismiss]);

    return {
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
    };
}
